import React, { useEffect, useState, useContext } from 'react';
import { Elements, useStripe } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { useHistory, useLocation } from 'react-router-dom';
import { createOrder } from '../../../api/orders';
import buildOrderPayload from './orderPayload';
import { CustomerContext } from '../../../contexts/CustomerContext';
import styles from './StripeTestCheckout.module.scss';

const stripePromise = loadStripe(process.env.STRIPE_PUBLISHABLE_KEY);

const CompleteInner = () => {
  const stripe = useStripe();
  const history = useHistory();
  const location = useLocation();
  const { customer, setCustomer } = useContext(CustomerContext);
  const [message, setMessage] = useState('');
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!stripe) {
      return;
    }

    const clientSecret = new URLSearchParams(location.search).get(
      'payment_intent_client_secret'
    );

    if (!clientSecret) {
      setMessage('No payment information found.');
      setIsLoading(false);
      setTimeout(() => history.push('/'), 3000);
      return;
    }

    stripe.retrievePaymentIntent(clientSecret).then(async ({ paymentIntent }) => {
      setIsLoading(false);

      switch (paymentIntent.status) {
        case 'succeeded': {
          setMessage('Payment succeeded! Creating order...');

          // Fetch the intent first: ITS metadata names the cart and email the
          // money was actually taken for (stamped server-side at creation).
          // buildOrderPayload binds the order to THAT cart; sessionStorage only
          // contributes contact/shipping, and only when it matches.
          let paymentIntentData = {};
          try {
            const paymentIntentResponse = await fetch(`/api/v1/payment-intent/${paymentIntent.id}`);
            paymentIntentData = await paymentIntentResponse.json();
          } catch (e) {
            paymentIntentData = {};
          }

          const orderPayload = buildOrderPayload({
            intentData: paymentIntentData,
            paymentIntentId: paymentIntent.id,
            storedCartId: sessionStorage.getItem('test_checkout_cart_id'),
            storedEmail: sessionStorage.getItem('test_checkout_email'),
            storedCustomerDataJson: sessionStorage.getItem('test_checkout_customer_data'),
          });

          if (orderPayload) {
            try {
              const response = await createOrder({ order: orderPayload });

              // Clear session storage
              sessionStorage.removeItem('test_checkout_cart_id');
              sessionStorage.removeItem('test_checkout_email');
              sessionStorage.removeItem('test_checkout_customer_data');

              // Clear cart from customer context
              setCustomer({ ...customer, cart: null });

              // Redirect to order complete page
              history.push(`/order/complete/${response.data.order.id}/${response.data.order.email}`);
            } catch (error) {
              console.error('Order creation error:', error);
              setMessage('Payment succeeded but order creation failed. Please contact support.');
              setTimeout(() => history.push('/'), 3000);
            }
          } else {
            // The charge is captured but we cannot name the cart -- NEVER
            // report this as plain success (a paid-but-no-order state).
            console.error('Order creation skipped: no cart identity on intent', paymentIntent.id);
            setMessage('Payment succeeded but your order could not be created. Please contact support.');
            setTimeout(() => history.push('/'), 5000);
          }
          break;
        }
        case 'processing':
          setMessage('Your payment is processing. Redirecting...');
          setTimeout(() => history.push('/'), 3000);
          break;
        case 'requires_payment_method':
          setMessage('Your payment was not successful, please try again.');
          setTimeout(() => history.push('/test-checkout'), 3000);
          break;
        default:
          setMessage('Something went wrong. Redirecting...');
          setTimeout(() => history.push('/'), 3000);
          break;
      }
    }).catch((error) => {
      setIsLoading(false);
      setMessage('Error verifying payment. Redirecting...');
      setTimeout(() => history.push('/'), 3000);
    });
  }, [stripe, location, history]);

  return (
    <div className={styles.container}>
      <div className={styles.card}>
        <h1 className={styles.title}>Processing Payment</h1>

        {isLoading ? (
          <div className={styles.loadingState}>
            <div className={styles.spinner}></div>
            <p>Verifying your payment...</p>
          </div>
        ) : (
          <div className={message.includes('succeeded') ? styles.successMessage : styles.errorMessage}>
            {message}
          </div>
        )}
      </div>
    </div>
  );
};

const Complete = () => {
  return (
    <Elements stripe={stripePromise}>
      <CompleteInner />
    </Elements>
  );
};

export default Complete;
